import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ReservationFormParams } from "../../store/draftReservation/types";

import type { V2CartServicesUpsert } from "@stepapp/core/apiTypes";
import { Block, Button, SvgIcon } from "@stepapp/ui";

import { BundleAdditionalFragment } from "@stepapp/core";
import ErrorMessage from "../ErrorMessage";
import IncludedInPriceItem from "./IncludedInPriceItem";
import QuantityAnswerItem from "./QuantityAnswerItem";
import { ServiceSceneDescription, ServiceSceneHeader } from "./ServiceSceneHeader";

type Props = {
  questions: BundleAdditionalFragment[];
  additionalServiceTitle: string;
  additionalServiceDescription: string;
  showCustomAdditionalDetails: boolean;
};

const QuantityQuestions = ({
  questions,
  additionalServiceTitle,
  additionalServiceDescription,
  showCustomAdditionalDetails,
}: Props) => {
  const { t } = useTranslation();

  const [showMoreOptions, setShowMoreOption] = useState(false);
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ReservationFormParams>();
  const services = watch("services");

  const handleShowMoreOptions = useCallback(() => {
    setShowMoreOption((oldValue) => !oldValue);
  }, []);

  const setNewServices = useCallback(
    (services: V2CartServicesUpsert[]) => {
      setValue("services", services, { shouldDirty: true, shouldTouch: true });
    },
    [setValue],
  );

  const servicesIncludedInPrice = questions?.filter((service) =>
    service?.variants?.some((variant) => variant?.isFree),
  );

  const servicesToOrder = questions?.filter(
    (service) => !service?.variants?.some((variant) => variant?.isFree),
  );

  const slicedQuestions = servicesToOrder.slice(0, 4);
  const visibleQuestions = showMoreOptions ? servicesToOrder : slicedQuestions;

  if (questions?.length === 0) return null;

  return (
    <Block marginTop={1} id="services" testID="additionalServices">
      <ServiceSceneHeader
        name={
          showCustomAdditionalDetails
            ? additionalServiceTitle
            : t("screens.service.additionalServices")
        }
      />

      <ServiceSceneDescription
        description={
          showCustomAdditionalDetails
            ? additionalServiceDescription
            : t("screens.service.additionalServicesDescription")
        }
      />

      <Block flex={1} marginTop={3}>
        {servicesIncludedInPrice?.map(({ icon, id, name, description }) => (
          <IncludedInPriceItem description={description} key={id} icon={icon} name={name} />
        ))}

        {visibleQuestions?.map(({ icon, id, name, description, variants }) => (
          <QuantityAnswerItem
            description={description}
            key={id}
            icon={icon}
            id={id}
            name={name}
            variants={variants}
            setNewServices={setNewServices}
            services={services}
          />
        ))}

        {servicesToOrder.length > 4 && (
          <Block alignStart flex={1}>
            <Button
              label={t("screens.service.showMoreServices")}
              onPress={handleShowMoreOptions}
              rightComponent={() => (
                <Block marginLeft={1.2}>
                  <SvgIcon name={showMoreOptions ? "chevron-up" : "chevron-down"} size={20} />
                </Block>
              )}
              type="secondary"
              variant="small"
            />
          </Block>
        )}

        <ErrorMessage message={errors?.services?.message} />
      </Block>
    </Block>
  );
};

export default QuantityQuestions;
